import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardController from '../Controllers/DashboardController';
import apiClient from '../Utils/apiClient';
import CompanySelect from '../Components/CompanySelect';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const CARD_CONFIG = [
  {
    key: 'todayVerifications',
    title: "Today's Verifications",
    subtitle: 'Last 24 hours',
    icon: 'bi-shield-check',
  },
  {
    key: 'failedAttempts',
    title: 'Failed Attempts',
    subtitle: 'This Month',
    icon: 'bi-exclamation-triangle',
  },
  {
    key: 'avgResponseMs',
    title: 'Avg Response',
    subtitle: 'Time to Verify · This Month',
    subdes: 'the average time it takes users to complete a verification, from requesting a code to successfully logging in with it.',
    icon: 'bi-speedometer2',
    format: (v) => {
      if (!v || v <= 0) return '--';
      if (v < 1000) return `${v} ms`;
      const totalSeconds = v / 1000;
      if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.round(totalSeconds % 60);
      return `${minutes}m ${seconds}s`;
    },
  },
];

function trailingMonthLabels(count) {
  const labels = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(d.toLocaleString('default', { month: 'short' }));
  }
  return labels;
}

export function Home() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['Admin', 'SuperAdmin'].includes(auth?.role);

  const todayStr = new Date().toISOString().slice(0, 10);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [otpToday, setOtpToday] = useState({ data: [], total: 0 });
  const [otpTodayLoading, setOtpTodayLoading] = useState(true);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    isAdmin ? '' : (auth?.companyId || '')
  );
  const [companiesLoading, setCompaniesLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    setCompaniesLoading(true);
    apiClient.get('companies')
    .then((res) => {
        const companyList = (res.data?.data || res.data || []).sort((a, b) =>
          a.name_firma.localeCompare(b.name_firma, undefined, {
            sensitivity: 'base',
          })
        );
        setCompanies(companyList);
      })
      .catch((err) => console.error('Failed to load companies', err))
      .finally(() => setCompaniesLoading(false));
  }, [isAdmin]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const companyIdToFetch = isAdmin ? selectedCompanyId : auth?.companyId;
      const data = await DashboardController.getOverview(companyIdToFetch);
      setOverview(data);
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedCompanyId, auth?.companyId]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const fetchOtpToday = useCallback(async () => {
    setOtpTodayLoading(true);
    try {
      const companyIdToFetch = isAdmin ? selectedCompanyId : auth?.companyId;
      const params = { limit: 30 };
      if (companyIdToFetch) params.company_id = companyIdToFetch;
      const data = await DashboardController.getOtpRequestsToday(params);
      setOtpToday(data);
    } catch (err) {
      console.error("Failed to load today's OTP requests", err);
    } finally {
      setOtpTodayLoading(false);
    }
  }, [isAdmin, selectedCompanyId, auth?.companyId]);

  useEffect(() => {
    fetchOtpToday();
  }, [fetchOtpToday]);

  const handleViewAllOtp = () => {
    const companyIdToFetch = isAdmin ? selectedCompanyId : auth?.companyId;
    navigate('/logs', {
      state: {
        action: 'OTP_REQUESTED',
        from: todayStr,
        to: todayStr,
        companyId: companyIdToFetch || '',
        initialData: otpToday.data,
        initialTotal: otpToday.total,
      },
    });
  };

  const handleCompanyChange = (id) => {
    setSelectedCompanyId(id);
  };
  const selectedCompany = companies.find(
    (c) => String(c.id) === String(selectedCompanyId)
  );
  const selectedLabel = isAdmin
    ? selectedCompany
      ? `${selectedCompany.name_firma} (${selectedCompany.short_name})`
      : 'All companies'
    : auth?.companyName;

  const monthlyStats = overview?.monthlyStats ?? [];

  const monthLabels = useMemo(() => {
    if (monthlyStats.length) return monthlyStats.map((m) => m.label);
    return trailingMonthLabels(7);
  }, [monthlyStats]);

  const successRateHistory = useMemo(() => {
    if (monthlyStats.length) return monthlyStats.map((m) => Number(m.successRate) || 0);
    return monthLabels.map(() => 0);
  }, [monthlyStats, monthLabels]);

  const otpSentHistory = useMemo(() => {
    if (monthlyStats.length) return monthlyStats.map((m) => Number(m.otpSent) || 0);
    return monthLabels.map(() => 0);
  }, [monthlyStats, monthLabels]);

  const otpUsedHistory = useMemo(() => {
    if (monthlyStats.length) return monthlyStats.map((m) => Number(m.otpUsed) || 0);
    return monthLabels.map(() => 0);
  }, [monthlyStats, monthLabels]);

  const successDelta = overview
    ? (successRateHistory[successRateHistory.length - 1] - successRateHistory[successRateHistory.length - 2]).toFixed(2)
    : '0.00';

  const otpSent = overview?.otpSent ?? 0;
  const otpUsed = overview?.otpDelivered ?? 0;

  const lineChartData = {
    labels: monthLabels,
    datasets: [
      {
        data: successRateHistory,
        borderColor: '#22c55e',
        backgroundColor: '#22c55e',
        pointBackgroundColor: '#22c55e',
        pointRadius: 4,
        tension: 0.45,
        borderWidth: 2,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { callback: (v) => `${v}%`, color: '#8a93a7' },
        grid: { color: '#f0f2f8' },
      },
      x: { grid: { display: false }, ticks: { color: '#8a93a7' } },
    },
  };

  const barChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'OTP Sent',
        data: otpSentHistory,
        backgroundColor: '#2f5fdb',
        borderRadius: 4,
        maxBarThickness: 18,
      },
      {
        label: 'OTP Used',
        data: otpUsedHistory,
        backgroundColor: '#22c55e',
        borderRadius: 4,
        maxBarThickness: 18,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { display: false, grid: { display: false } },
      x: { grid: { display: false }, ticks: { color: '#8a93a7' } },
    },
  };

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">
              Welcome to<br />Secure Identity Verification!
            </h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              {selectedLabel}
            </p>
          </div>

          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            {isAdmin && (
              <CompanySelect
                companies={companies.filter((c) => c.status === true && c.enable_verification === true)}
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                placeholder="All companies"
                loading={companiesLoading}
                variant="pill"
              />
            )}

            <button
              className="ivs-icon-btn"
              onClick={fetchOverview}
              disabled={loading}
              aria-label="Refresh"
              type="button"
            >
              <i className={`bi ${loading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'}`}></i>
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        {loading ? (
          <div className="overview-skeleton mb-4">
            {CARD_CONFIG.map((c) => (
              <div key={c.key} className="stat-card stat-card--loading">
                <div className="skeleton-line short" />
                <div className="skeleton-line tall" />
                <div className="skeleton-line short" />
              </div>
            ))}
          </div>
        ) : (
          overview && (
            <div className="ivs-stat-grid">
              {CARD_CONFIG.map((card) => {
                const raw = overview[card.key] ?? 0;
                const display = card.format ? card.format(raw) : Number(raw).toLocaleString();
                return (
                  <div key={card.key} className="ivs-stat-card">
                    <div className="ivs-stat-card__body">
                      <div className="ivs-stat-card__value">{display}</div>
                      <div className="ivs-stat-card__heading">
                        <div className="ivs-stat-card__label">{card.subtitle}</div>
                        <div className="ivs-stat-card__title">{card.title}</div>
                        <div className="ivs-stat-card__label">{card.subdes}</div>
                      </div>
                    </div>
                    <div
                      className="ivs-stat-card__icon"
                      style={{ background: 'var(--ivs-primary-light)', color: 'var(--ivs-primary)' }}
                    >
                      <i className={`bi ${card.icon}`}></i>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {!loading && overview && (
          <div className="ivs-panel-grid">
            <div className="ivs-panel">
              <div className="ivs-panel__header">
                <h3 className="ivs-panel__title">Success Rate</h3>
                <div>
                  <div className="ivs-panel__metric">{Number(overview.successRate ?? 0).toFixed(1)}%</div>
                  <div className="ivs-panel__delta">
                    <i className={`bi ${successDelta >= 0 ? 'bi-caret-up-fill' : 'bi-caret-down-fill'}`}></i>{' '}
                    {Math.abs(successDelta)}% This Month
                  </div>
                </div>
              </div>
              <div style={{ height: 260 }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </div>
              <div className="ivs-panel__footer">
                <a className="ivs-panel__link" href="/analytics">View Detail →</a>
              </div>
            </div>

            <div className="ivs-panel">
              <div className="ivs-panel__header">
                <h3 className="ivs-panel__title">OTP Detail</h3>
              </div>
              <div style={{ height: 220 }}>
                <Bar data={barChartData} options={barChartOptions} />
              </div>
              <div className="ivs-otp-legend">
                <div className="ivs-otp-legend__item">
                  <span className="ivs-otp-legend__swatch" style={{ background: '#2f5fdb' }}></span>
                  <div>
                    <span className="ivs-otp-legend__label">OTP Sent · This Month</span>
                    <span className="ivs-otp-legend__value">{otpSent.toLocaleString()}</span>
                  </div>
                </div>
                <div className="ivs-otp-legend__item">
                  <span className="ivs-otp-legend__swatch" style={{ background: '#22c55e' }}></span>
                  <div>
                    <span className="ivs-otp-legend__label">OTP Used · This month</span>
                    <span className="ivs-otp-legend__value">{otpUsed.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="ivs-panel__footer">
                <a className="ivs-panel__link" href="/analytics">View Detail →</a>
              </div>
            </div>
          </div>
        )}

        <div className="ivs-panel mt-4">
          <div className="ivs-panel__header">
            <h3 className="ivs-panel__title">OTP Requests Today</h3>
            <div className="ivs-panel__metric">{otpToday.total.toLocaleString()}</div>
          </div>

          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>Time</th>
                <th>Email / Mobile</th>
                {isAdmin && <th>Company</th>}
                <th>Method</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {otpTodayLoading ? (
                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center">Loading…</td></tr>
              ) : otpToday.data.length === 0 ? (
                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center">No OTP requests yet today</td></tr>
              ) : (
                otpToday.data.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.createdAt).toLocaleTimeString()}</td>
                    <td>{row.email || row.mobile || '—'}</td>
                    {isAdmin && <td>{row.company || '—'}</td>}
                    <td>{row.method}</td>
                    <td>
                      <span className={row.status === 'FAILED' ? 'badge badge-danger' : 'badge badge-success'}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      {row.email ? (
                        <a
                          className="ivs-icon-btn"
                          style={{ width: 30, height: 30 }}
                          href={`/analytics?email=${encodeURIComponent(row.email)}&from=${todayStr}&to=${todayStr}`}
                          aria-label={`View verification detail for ${row.email}`}
                          title="View verification detail"
                        >
                          <i className="bi bi-search" style={{ fontSize: 12 }}></i>
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="ivs-panel__footer">
            <button type="button" className="ivs-panel__link" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} onClick={handleViewAllOtp}>
              View All →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
